import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { datePickerClearableProps, datePickerFormItemProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { toDate } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Checkbox, Col, DatePicker, Form, Input, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateAgentBrokerNumber, useUpdateAgentBrokerNumber } from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import type { UUID } from "../../../../typings/global";
import InstitutionSelect from "../../../enumerations/components/form/InstitutionSelect";
import { AgentBrokerNumber, CreateUpdateAgentBrokerNumber } from "../../types";

interface Props {
  open: boolean;
  agentId: UUID;
  brokerNumber?: AgentBrokerNumber;
  onFormCancel: () => void;
}

const AgentBrokerNumberForm = ({ open, agentId, brokerNumber, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateAgentBrokerNumber>();
  const validFrom = Form.useWatch("validFrom", form);

  const createAgentBrokerNumber = useCreateAgentBrokerNumber();
  const updateAgentBrokerNumber = useUpdateAgentBrokerNumber();

  useEffect(() => {
    if (open && brokerNumber) {
      form.setFieldsValue({
        ...brokerNumber,
        institutionId: brokerNumber.institution.id,
      });
    }
  }, [open, brokerNumber, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (brokerNumber) {
          updateAgentBrokerNumber.mutate(
            { id1: agentId, id2: brokerNumber.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "agent.brokerNumber.attrs", error.violations),
            },
          );
        } else {
          createAgentBrokerNumber.mutate(
            { id: agentId, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "agent.brokerNumber.attrs", error.violations),
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={t(brokerNumber ? "agent.brokerNumber.titles.update" : "agent.brokerNumber.titles.create")}
      cancelText={t("common.cancel")}
      okText={t("common.save")}
      confirmLoading={createAgentBrokerNumber.isPending || updateAgentBrokerNumber.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={brokerNumber} />

      <Form form={form} layout="vertical" name="agentBrokerNumberForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={12}>
            <InstitutionSelect
              formItemProps={{
                name: "institutionId",
                label: t("agent.brokerNumber.attrs.institutionId"),
                rules: [validations.notNull],
              }}
              optionsProps={{
                selected: brokerNumber?.institution ? [brokerNumber.institution] : undefined,
                groupByType: true,
              }}
            />
          </Col>

          <Col span={12}>
            <Form.Item
              name="value"
              label={t("agent.brokerNumber.attrs.value")}
              rules={[validations.notBlank, validations.size(1, 64)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item
              name="validFrom"
              label={t("agent.brokerNumber.attrs.validFrom")}
              rules={[validations.none]}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerClearableProps} showNow={false} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="validTo"
              label={t("agent.brokerNumber.attrs.validTo")}
              rules={[
                validFrom
                  ? validations.notSameOrBefore(validFrom, t("agent.brokerNumber.attrs.validFrom"))
                  : validations.none,
              ]}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerClearableProps} showNow={false} minDate={toDate(validFrom)?.add(1, "day")} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item name="commonUsage" valuePropName="checked" rules={[validations.none]} initialValue={false}>
              <Checkbox>{t("agent.brokerNumber.attrs.commonUsage")}</Checkbox>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="useForContractMatching"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox>{t("agent.brokerNumber.attrs.useForContractMatching")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item name="note" label={t("agent.brokerNumber.attrs.note")} rules={[validations.size(1, 8192)]}>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AgentBrokerNumberForm;
